import { Link } from "react-router-dom";
import { useDevice } from "../../context/DeviceProvider";
import { LucideIcon, type LucideIconName } from "../FG/LucideIcon";
import { ReassuranceStrip } from "./ReassuranceStrip";
import { useLandingDoorActions } from "./useLandingDoorActions";
// One webp candidate per photo (the 400w rungs were unreachable at DPR ≥ 2 and
// have been dropped) + the jpg `src` fallback. `sizes` stay truthful — they
// mostly guide preload heuristics with a single candidate.
import heroFuelPlanImg from "./hero-fuel-plan.jpg";
import heroFuelPlan724 from "./hero-fuel-plan-724.webp";
import whoRunnerImg from "./who-runner.jpg";
import whoRunner667 from "./who-runner-667.webp";
import sweepsCelebrateImg from "./sweeps-celebrate.jpg";
import sweepsCelebrate752 from "./sweeps-celebrate-752.webp";
import "./editorialLanding.css";

const SWEEPSTAKES_TERMS_URL =
  "https://fuelgoods.com/pages/fuel-goods-fuel-your-finish-sweepstakes-terms-and-conditions";

/**
 * Landing "1a — The Editorial" (light, opinionated, education-first).
 *
 * Long-form editorial landing rendered INSIDE the Shopify storefront theme —
 * the theme supplies the site header + footer, so this owns no top nav / logo
 * footer. One contained rounded "card" (`.fgl-ed`) with internal full-width
 * colour bands. Every colour/shadow/radius/type comes from the design tokens
 * (`styles/tokens.css`); new `fgl-ed-*` classes only cover layout the shared
 * primitives don't.
 *
 * One primary action everywhere: every "Build my fueling plan" CTA (hero,
 * how-it-works, who-it's-for, final band) plus the sweepstakes CTA opens the
 * plan wizard via {@link useLandingDoorActions}.
 *
 * Testimonials + Sweepstakes are flag-gated OFF by default; the sweepstakes is
 * campaign-gated.
 */
export type EditorialLandingProps = {
  showTestimonials?: boolean;
  /** Campaign band — flip on during a sweepstakes. */
  showSweepstakes?: boolean;
};

const STEPS = [
  {
    n: 1,
    t: "Plan your training",
    b: "Bring a race, import your coach's plan, or build your own week. Move things around whenever life does.",
    accent: false
  },
  {
    n: 2,
    t: "We build your fuel",
    b: "Every session gets sized — carbs, sodium, caffeine, pre / during / post. No spreadsheets, no guessing grams.",
    accent: true
  },
  {
    n: 3,
    t: "It arrives, box by box",
    b: "Fresh boxes matched to what's ahead. Review and buy each one as you go, whenever you're ready — no subscription, nothing charged automatically.",
    accent: false,
    rail: true
  }
];

/** "What's in a box" pills — Lucide glyphs (VIS-04; emoji retired). */
const BOX_PILLS: { icon: LucideIconName; label: string }[] = [
  { icon: "zap", label: "Carbs" },
  { icon: "sparkles", label: "Sodium" },
  { icon: "cup", label: "Caffeine" },
  { icon: "droplet", label: "Hydration" },
  { icon: "heart", label: "Recovery" }
];

const PERSONAS: { emoji: string; t: string; b: string }[] = [
  {
    emoji: "🌱",
    t: "New to fueling",
    b: "We ease you into fueling and build up week by week, so your stomach is not getting a surprise party on race day."
  },
  {
    emoji: "🎯",
    t: "Chasing a BQ",
    b: "Dialed to the session. Carb targets, sodium and caffeine timed so nothing's left to chance."
  },
  {
    emoji: "🏁",
    t: "Just want to finish",
    b: "Tell us the race. We’ll handle the fuel. You show up ready, run your heart out, and enjoy every finish-line feeling."
  },
  {
    emoji: "⚡",
    t: "Serial swappers",
    b: "Hate a flavor? Have flavor fatigue? Swap it — one workout or your whole plan. Rotate fuel as often as you like."
  }
];

const TESTIMONIALS = [
  {
    quote:
      "This has been an absolute game changer. I wish I had this for my first marathon.",
    name: "Mike B.",
    meta: "10+ marathons",
    avatar: "var(--fg-purple-100)"
  },
  {
    quote:
      "I've spent years overthinking grams. This did it in two minutes and it was better than my spreadsheet.",
    name: "Marcus T.",
    meta: "BQ chaser · Indy Monumental",
    avatar: "var(--fg-lime-pale)"
  },
  {
    quote:
      "I'm helping two athletes right now who are running their first marathon in NYC and they needed this desperately.",
    name: "Brandon E.",
    meta: "Running coach · 40+ athletes",
    avatar: "var(--fg-yellow-pale)"
  }
];

const FAQS = [
  {
    q: "Do I have to subscribe?",
    a: "No — there's no subscription. No lock-in, no countdown timers, no auto-charges. You review and buy each box as you go, so you only ever pay for what's in your cart."
  },
  {
    q: "What does a box cost?",
    a: "It depends on how often boxes arrive and how much training they cover — from about $25 for a light week up to $100+ for a big multi-week box. You always see the exact price before anything goes in your cart."
  },
  {
    q: "I've never really fueled before. Is this for me?",
    a: "Especially for you. We ease you into fueling, so your stomach is not getting a surprise party on race day."
  },
  {
    q: "Can I use my coach's plan?",
    a: "Yes — import it, build your own, or run ours. The fuel adapts to whatever you're actually doing."
  },
  {
    q: "What if my training changes?",
    a: "It will. Move a workout and the fuel moves with it. That's the whole point."
  },
  {
    q: "Is this just your own products?",
    a: "For now, yes — the recommendations are products found on the Fuel Goods marketplace. Ones we know and can ship to you. Don't love something? Swap it for another option that still hits your targets."
  },
  {
    q: "Do I need an account to start?",
    a: "Nope. Plan as a guest, sign in at checkout when you're ready to order."
  }
];

/**
 * Drops mini-rail inside how-it-works step 3 (CRO-05) — the shared `fgl-drops`
 * classes from landing.css with the step's own chips/caption.
 */
function StepDropsRail() {
  return (
    <div className="fgl-preview fgl-preview--drops fgl-ed__step-rail" aria-hidden="true">
      <div className="fgl-drops">
        <div className="fgl-drops__rail">
          <span className="fgl-drops__chip fgl-drops__chip--done">Wks 1–2 ✓</span>
          <span className="fgl-drops__chip fgl-drops__chip--active">Wks 3–4</span>
          <span className="fgl-drops__chip fgl-drops__chip--next">Wks 5–6</span>
          <span className="fgl-drops__chip fgl-drops__chip--race">Race day</span>
        </div>
        <div className="fgl-drops__caption">Your race-day plan is visible from day one.</div>
      </div>
    </div>
  );
}

function EditorialFuelCard({
  light = false,
  slot = "hero"
}: {
  light?: boolean;
  /** hero = floats over the hero photo; problem = stacked mobile-only;
      problem-wide = the problem band's desktop right column. */
  slot?: "hero" | "problem" | "problem-wide";
}) {
  return (
    <div
      className={[
        "fgl-ed__fuelcard-wrap",
        `fgl-ed__fuelcard-wrap--${slot}`,
        light ? "fgl-ed__fuelcard-wrap--light" : ""
      ]
        .filter(Boolean)
        .join(" ")}
      aria-hidden="true">
      <div className="fgl-ed__fuelcard">
        <div className="fgl-ed__fuelcard-label">Saturday · Long run · 16 mi</div>
        <div className="fgl-ed__fuelcard-macros">
          <div className="fgl-ed__macro fgl-ed__macro--pre">
            <span className="fgl-ed__macro-k">Pre</span>
            <span className="fgl-ed__macro-v fgl-ed__macro-v--pre">30g</span>
            <span className="fgl-ed__macro-u">carbs</span>
          </div>
          <div className="fgl-ed__macro fgl-ed__macro--during">
            <span className="fgl-ed__macro-k">During</span>
            <span className="fgl-ed__macro-v fgl-ed__macro-v--during">80g/h</span>
            <span className="fgl-ed__macro-u">carbs</span>
          </div>
          <div className="fgl-ed__macro fgl-ed__macro--post">
            <span className="fgl-ed__macro-k">Post</span>
            <span className="fgl-ed__macro-v fgl-ed__macro-v--post">25g</span>
            <span className="fgl-ed__macro-u">protein</span>
          </div>
        </div>
        <div className="fgl-ed__fuelcard-foot">
          <span>
            <strong>640mg</strong> sodium
          </span>
          <span>
            <strong>100mg</strong> caffeine
          </span>
        </div>
      </div>
      <div className="fgl-ed__fuelcard-badge">Customized for you ⚡</div>
    </div>
  );
}

export function EditorialLanding({
  showTestimonials = false,
  showSweepstakes = false
}: EditorialLandingProps = {}) {
  const { onApplyPlan } = useLandingDoorActions();
  const device = useDevice();

  return (
    <div className="fgl-ed" data-testid="landing">
      {/* ── HERO ── */}
      <section className="fgl-ed__band fgl-ed__hero">
        <div className="fgl-ed__hero-grid">
          <div className="fgl-ed__hero-copy">
            <div className="fg-eyebrow">Fueling Plans</div>
            <h1 className="fgl-ed__hero-title">
              Plan the miles.
              <br />
              We'll plan the <em>fuel.</em>
            </h1>
            <p className="fgl-ed__hero-lede">
             Our tool turns your training into a <strong>personalized fueling plan</strong>
              — pre, during, and post, session by session.
            </p>
            <div className="fgl-ed__hero-cta">
              <button
                type="button"
                className="fg-btn fg-btn--purple fg-btn--lg"
                onClick={onApplyPlan}>
                Build my fueling plan →
              </button>
            </div>
            <p className="fgl-guest-note fgl-ed__hero-note">
              Free · no account needed · about 5 minutes.
            </p>
            <a href="#how-it-works" className="fgl-ed__hero-how">
              See how it works ↓
            </a>
          </div>

          {/* Photo + the floating example fuel card (CRO-01) — the card hides
              when the hero stacks (≤1024px, in CSS). */}
          <div className="fgl-ed__hero-visual">
            <div className="fgl-ed__hero-img">
              <img
                className="fgl-ed__img-photo"
                src={heroFuelPlanImg}
                srcSet={`${heroFuelPlan724} 724w`}
                // Stacked (≤1024): the visual column caps at 420px. Desktop:
                // the 1fr column of the 1.35fr/1fr hero grid ≈ 42vw, ≤558px
                // under the 1400px page cap (editorialLanding.css).
                sizes="(max-width: 1024px) min(420px, 100vw), min(42vw, 558px)"
                width={724}
                height={543}
                alt="A runner holding a printed San Francisco Marathon fueling plan at the race start"
                loading="eager"
              />
            </div>
            <EditorialFuelCard />
          </div>
        </div>

        <ReassuranceStrip
          layout={device === "mobile" ? "mobile" : device === "tablet" ? "stack" : "row"}
        />
      </section>

      {/* ── SWEEPSTAKES (flag-gated; campaign copy) ── */}
      {showSweepstakes ? (
        <section id="win" className="fgl-ed__band fgl-ed__sweeps">
          <div className="fgl-ed__sweeps-glow" aria-hidden="true" />
          <div className="fgl-ed__sweeps-inner">
            <div className="fgl-ed__sweeps-main">
              <h2 className="fgl-ed__h2 fgl-ed__sweeps-h">
                Win
                <br />
                <span className="fgl-ed__sweeps-prize">up to $1,200</span>
                <br />
                in fuel<span className="fgl-ed__sweeps-prize">.</span>
              </h2>
              <p className="fgl-ed__lede-dim fgl-ed__sweeps-lede">
                Get your free personalized Fueling Plan and you're automatically entered to win
                every product in it.
              </p>
              <div className="fgl-ed__sweeps-cta">
                <button
                  type="button"
                  className="fg-btn fg-btn--lime fg-btn--lg"
                  onClick={onApplyPlan}>
                  Get my free plan to enter →
                </button>
              </div>
              <p className="fgl-ed__sweeps-fine">
                <svg
                  className="fgl-ed__sweeps-timer"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true">
                  <line x1="10" y1="2" x2="14" y2="2" />
                  <line x1="12" y1="14" x2="15" y2="11" />
                  <circle cx="12" cy="14" r="8" />
                </svg>
                <span>About 5 minutes. We might pick up the entire tab.</span>
              </p>
              <p className="fgl-ed__sweeps-rules">
                No purchase necessary. Void where prohibited. See{" "}
                <a
                  className="fgl-ed__sweeps-rules-link"
                  href={SWEEPSTAKES_TERMS_URL}
                  target="_blank"
                  rel="noopener noreferrer">
                  Terms &amp; Conditions
                </a>{" "}
                for details.
              </p>
            </div>
            <div className="fgl-ed__sweeps-img">
              <img
                className="fgl-ed__img-photo"
                src={sweepsCelebrateImg}
                srcSet={`${sweepsCelebrate752} 752w`}
                // Stacked (≤820): the image caps at 340px. Desktop: the 1fr
                // column of the 1.2fr/1fr sweeps grid ≈ 43vw, ≤593px under
                // the 1400px page cap (editorialLanding.css).
                sizes="(max-width: 820px) 340px, min(43vw, 593px)"
                width={752}
                height={1000}
                alt="A runner celebrating at the finish line, arms raised as confetti falls"
                loading="lazy"
              />
            </div>
          </div>
        </section>
      ) : null}

      {/* ── PROBLEM (light lavender editorial moment) ── */}
      <section className="fgl-ed__band fgl-ed__band--soft fgl-ed__problem">
        {/* Copy left, fuel card right on desktop (VIS-02); single column when
            stacked — the mobile-only `slot="problem"` card below takes over. */}
        <div className="fgl-ed__problem-grid">
          <div>
            <div className="fg-eyebrow">The problem</div>
            <h2 className="fgl-ed__h2 fgl-ed__problem-h">
              The miles are planned. The fuel is freelancing.
            </h2>
            <p className="fgl-ed__problem-lede">
              A gel because the aid station had them. Sixty grams an hour because a podcast said so
              — or was it ninety?
            </p>
            <p className="fgl-ed__problem-lede fgl-ed__problem-lede--follow">
              Fueling by feel is how good training quietly falls apart at mile 20. The industry is
              fine with that. Confusion sells more gels.
            </p>
            <p className="fgl-ed__problem-lede fgl-ed__problem-lede--strong">
              We're not the industry. We do the math once, tie it to your actual sessions, and hand
              you a number you can trust.
            </p>
          </div>
          <EditorialFuelCard slot="problem-wide" />
        </div>
        <EditorialFuelCard slot="problem" />
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="fgl-ed__band fgl-ed__band--paper">
        <div className="fg-eyebrow">How it works</div>
        <h2 className="fgl-ed__h2">Three steps. Your fuel at your door.</h2>
        <p className="fgl-ed__how-lede">
          Set it up once. Change a workout whenever — the fuel keeps up on its own.
        </p>
        <div className="fgl-ed__steps">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className={`fgl-ed__step${s.accent ? " fgl-ed__step--accent" : ""}`}>
              <div className={`fgl-ed__step-num${s.accent ? " fgl-ed__step-num--accent" : ""}`}>
                {s.n}
              </div>
              <div className="fgl-ed__step-body">
                <h3 className="fgl-ed__step-t">{s.t}</h3>
                <p className="fgl-ed__step-b">{s.b}</p>
                {s.rail ? <StepDropsRail /> : null}
              </div>
            </div>
          ))}
        </div>
        <div className="fgl-ed__how-cta">
          <button
            type="button"
            className="fg-btn fg-btn--purple fg-btn--lg"
            onClick={onApplyPlan}>
            Build my fueling plan →
          </button>
        </div>
      </section>

      {/* ── WHAT'S IN A BOX ── */}
      <section id="whats-in-a-box" className="fgl-ed__band fgl-ed__band--lime">
        <div className="fgl-ed__box-grid">
          <div>
            <div className="fg-eyebrow fgl-ed__eyebrow--olive">What's in a box</div>
            <h2 className="fgl-ed__h2">Fuel that matches your training plan.</h2>
            <p className="fgl-ed__box-copy">
            Each box is built around the sessions ahead. You can review it, swap flavors or products, 
            personalize what you want, and add it to cart when you’re ready.
            </p>
            <div className="fgl-ed__pills">
              {BOX_PILLS.map((p) => (
                <span key={p.label} className="fgl-ed__pill">
                  <LucideIcon name={p.icon} size={13} color="var(--fg-purple-deep)" />
                  {p.label}
                </span>
              ))}
            </div>
          </div>
          <div className="fgl-ed__box-card">
            <div className="fgl-ed__box-card-head">
              <div className="fgl-ed__box-card-title">Saturday · 16 mi long run</div>
              <span className="fg-tag fg-tag--purple fgl-ed__box-card-tag">Long-day stack</span>
            </div>
            <div className="fgl-ed__box-macros">
              <div className="fgl-ed__box-macro">
                <div className="fgl-ed__box-macro-k">Pre</div>
                <div className="fgl-ed__box-macro-v">30g</div>
                <div className="fgl-ed__box-macro-u">carbs · 30–60 min before</div>
              </div>
              <div className="fgl-ed__box-macro fgl-ed__box-macro--hi">
                <div className="fgl-ed__box-macro-k fgl-ed__box-macro-k--hi">During</div>
                <div className="fgl-ed__box-macro-v">
                  80<span className="fgl-ed__box-macro-sm">g/h</span>
                </div>
                <div className="fgl-ed__box-macro-u">carbs + 640mg sodium</div>
              </div>
              <div className="fgl-ed__box-macro">
                <div className="fgl-ed__box-macro-k">Post</div>
                <div className="fgl-ed__box-macro-v">25g</div>
                <div className="fgl-ed__box-macro-u">protein · &lt; 30 min</div>
              </div>
            </div>
            <div className="fgl-ed__box-card-foot">
              <div className="fgl-ed__box-card-note">
                4 products · example fueling plan targets
              </div>
              <span className="fgl-ed__box-card-cta">Add box · $18 →</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO IT'S FOR ── */}
      <section id="who-its-for" className="fgl-ed__band fgl-ed__band--paper">
        <div className="fg-eyebrow">Who it's for</div>
        <h2 className="fgl-ed__h2 fgl-ed__h2--cap">
          Whether it's your first mile or your fastest.
        </h2>
        <div className="fgl-ed__who-grid">
          <div className="fgl-ed__personas">
            {PERSONAS.map((p) => (
              <div key={p.t} className="fgl-ed__persona">
                <div className="fgl-ed__persona-emoji">{p.emoji}</div>
                <h3 className="fgl-ed__persona-t">{p.t}</h3>
                <p className="fgl-ed__persona-b">{p.b}</p>
              </div>
            ))}
          </div>
          <div className="fgl-ed__who-img">
            <img
              className="fgl-ed__img-photo"
              src={whoRunnerImg}
              srcSet={`${whoRunner667} 667w`}
              // Stacked (≤820): full content column = 100vw minus the band
              // gutters (not 100vw). Desktop: the 1fr column of the 2fr/1fr
              // who-grid ≈ 31vw, ≤443px under the 1400px page cap.
              sizes="(max-width: 820px) calc(100vw - 32px), min(31vw, 443px)"
              width={667}
              height={1000}
              alt="A runner powering through a half marathon, mid-stride"
              loading="lazy"
            />
          </div>
        </div>
        <div className="fgl-ed__who-cta">
          <button
            type="button"
            className="fg-btn fg-btn--purple fg-btn--lg"
            onClick={onApplyPlan}>
            Build my fueling plan →
          </button>
        </div>
      </section>

      {/* ── TESTIMONIALS (flag-gated; placeholder quotes) ── */}
      {showTestimonials ? (
        <section className="fgl-ed__band fgl-ed__band--soft">
          <div className="fg-eyebrow">From runners</div>
          <h2 className="fgl-ed__h2">People stopped guessing. Here's what happened.</h2>
          <div className="fgl-ed__quotes">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="fgl-ed__quote">
                <div className="fg-stars">★★★★★</div>
                <p className="fgl-ed__quote-text">"{t.quote}"</p>
                <div className="fgl-ed__quote-by">
                  <span>
                    <span className="fgl-ed__quote-name">{t.name}</span>
                    <span className="fgl-ed__quote-meta">{t.meta}</span>
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {/* ── FAQ ── */}
      <section id="faq" className="fgl-ed__band fgl-ed__band--app">
        <div className="fg-eyebrow">Straight answers</div>
        <h2 className="fgl-ed__h2">Questions we'd ask too.</h2>
        <div className="fgl-ed__faq">
          {FAQS.map((f) => (
            <div key={f.q} className="fgl-ed__faq-item">
              <h3 className="fgl-ed__faq-q">{f.q}</h3>
              <p className="fgl-ed__faq-a">{f.a}</p>
            </div>
          ))}
        </div>
        {/* Full FAQ library lives on its own page (/faq). The state flag lets its
            Back button return to this FAQ section instead of the top of the page. */}
        <div style={{ marginTop: 28 }}>
          <Link to="/faq" state={{ fromLandingFaq: true }} className="fg-btn fg-btn--soft">
            See more FAQs →
          </Link>
        </div>
      </section>

      {/* ── FINAL CTA (light callout wash) ── */}
      <section className="fgl-ed__band fgl-ed__final">
        <h2 className="fgl-ed__h2 fgl-ed__final-h">
          Plan the miles.
          <br />
          We'll plan the fuel.
        </h2>
        <p className="fgl-ed__final-lede">
          About five minutes to a fueling plan that actually matches your training. No account
          needed to start.
        </p>
        <div className="fgl-ed__final-cta-row">
          <button
            type="button"
            className="fg-btn fg-btn--purple fg-btn--lg"
            onClick={onApplyPlan}>
            Build my fueling plan →
          </button>
        </div>
        <p className="fgl-ed__final-note">
          No account needed · Buy box by box · Standard 3–6 day shipping
        </p>
      </section>
    </div>
  );
}
